import { styled } from '@mui/material'
import { animated } from '@react-spring/web'
import { SyntheticEvent, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { BucketMedia } from '../../../../graphql/generated'
import { imagePlaceholder } from '../../utils/placeholders'
import VideoPlayer from './VideoPlayer'
import usePinchZoom from '../../hooks/usePinchZoom'

interface Props {
  media: BucketMedia
  isSelected: boolean
}

interface MediaProps {
  isLoading?: boolean
  isBlurred?: boolean
}

const StyledImage = styled('img', {
  shouldForwardProp: (prop: string) => prop !== 'isLoading' && prop !== 'isBlurred'
})<MediaProps>(({ isBlurred, isLoading }) => ({
  userSelect: isLoading ? 'none' : undefined,
  WebkitTouchCallout: isLoading ? 'none' : undefined,
  objectFit: 'contain',
  filter: isBlurred ? 'blur(5px) brightness(95%)' : 'none',
  width: '100%',
  height: '100%'
}))

export default function LoadImage({ media, isSelected }: Props) {
  const [isLoading, setIsLoading] = useState(true)
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const action = params.get('action')

  // Determine Pinch to zoom settings
  const width = media?.width ?? 0
  const height = media?.height ?? 0
  const bounds = { left: -width / 2, right: width / 2, top: -height / 2, bottom: height / 2 }
  const { ref, style } = usePinchZoom(bounds)

  // Determine image optomized urls

  let src = media?.imageUrl
  if (!media.isBanned && src && (action === 'zoom' || action === 'focus')) {
    src += `?height=${height}&width=${width}&quality=100`
  }

  // Event handlers
  const onLoad = () => setIsLoading(false)

  const handleError = (e: SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = imagePlaceholder
    e.currentTarget.srcset = imagePlaceholder
  }

  if (!media.isBanned && media.isVideo && isSelected) {
    return <VideoPlayer media={media} />
  }

  if (!src) {
    return null
  }

  return (
    <animated.div style={{ touchAction: 'none', ...style }} ref={ref}>
      <StyledImage
        draggable={false}
        id="full-image"
        isLoading={isLoading}
        src={src}
        srcSet={src}
        alt={media.id}
        onLoad={onLoad}
        ref={ref}
        onError={handleError}
      />
    </animated.div>
  )
}
